import { useTranslation } from "react-i18next";
import { HiCursorClick, HiLink, HiPencil } from "react-icons/hi";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import useAuth from "../../../../hooks/useAuth";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import { PlansModal } from "../../../modals/PlansModal";
import { ModalShell } from "../../../atoms/ModalShell";
import { useState } from "react";
import useRolePermissions from "../../../../utils/rolePermissions";

interface AddUserTypeSelectionModalProps {
  show: boolean;
  onHide: () => void;
  onSelectType: (type: "au" | "ma" | "sh") => void;
}

const PointerIcon = () => {
  return <HiCursorClick size={24} className="text-blue-200" />;
};

const FormIcon = () => {
  return <HiPencil size={24} className="text-blue-200" />;
};

const LinkIcon = () => {
  return <HiLink size={24} className="text-blue-200" />;
};

const AddUserTypeSelectionModal = ({
  show,
  onHide,
  onSelectType,
}: AddUserTypeSelectionModalProps) => {
  const [t] = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const rolePermissions = useRolePermissions(user);
  const [showPlansModal, setShowPlansModal] = useState(false);

  const canImportUsers = permissions.can(Cap.IMPORT_USERS, Level.WRITE);

  const handleSelectType = (type: "au" | "ma" | "sh") => {
    if (!canImportUsers) return;

    if (type === "au" && !permissions.isAtLeastTier(PlanTier.PREMIUM)) {
      setShowPlansModal(true);
      return;
    }

    onHide();
    setTimeout(() => {
      onSelectType(type);
    }, 100);
  };

  return (
    <>
      <ModalShell
        show={show}
        onClose={onHide}
        size="lg"
        title={t("dashboard.usersList.key1")}
      >
        <div className="space-y-3">
          {rolePermissions.isOwner && canImportUsers && (
            <button
              onClick={() => handleSelectType("au")}
              className="w-full p-4 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer border-none"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex-shrink-0">
                    <PointerIcon />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white text-base font-semibold font-figtree leading-tight flex items-center gap-2">
                      {t("dashboard.usersList.addUserModal.title-01")}
                      {!permissions.isAtLeastTier(PlanTier.PREMIUM) && (
                        <UnlockBadge />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6"></div>
                  <div className="flex-1 text-left text-blue-100 text-sm font-normal font-figtree leading-relaxed">
                    {t("dashboard.usersList.addUserModal.text-01")}
                  </div>
                </div>
              </div>
            </button>
          )}

          {canImportUsers && (
            <button
              onClick={() => handleSelectType("ma")}
              className="w-full p-4 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer border-none"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex-shrink-0">
                    <FormIcon />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white text-base font-semibold font-figtree leading-tight">
                      {t("dashboard.usersList.addUserModal.title-02")}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6"></div>
                  <div className="flex-1 text-left text-blue-100 text-sm font-normal font-figtree leading-relaxed">
                    {t("dashboard.usersList.addUserModal.text-02")}
                  </div>
                </div>
              </div>
            </button>
          )}

          {canImportUsers && (
            <button
              onClick={() => handleSelectType("sh")}
              className="w-full p-4 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors cursor-pointer border-none"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex-shrink-0">
                    <LinkIcon />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white text-base font-semibold font-figtree leading-tight">
                      {t("dashboard.usersList.addUserModal.title-03")}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6"></div>
                  <div className="flex-1 text-left text-blue-100 text-sm font-normal font-figtree leading-relaxed">
                    {t("dashboard.usersList.addUserModal.text-03")}
                  </div>
                </div>
              </div>
            </button>
          )}
        </div>
      </ModalShell>

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        targetPlan={PlanTier.PREMIUM}
      />
    </>
  );
};

export default AddUserTypeSelectionModal;
